import Effects from './Effects.js';
import Genetic from "effects/midi/mappable/Genetic.js";

import {bulkUpdate} from 'utils/bulkUpdate.js';
import Tone from 'tone';
import {AbstractParameter} from 'parameters/AbstractParameter.js';
import {AbstractAudioParamParameter,
        AudioParamValueParameter} from 'parameters/AbstractAudioParamParameter.js';
import {decibelsToLinear, linearToDecibels} from 'utils/db.js';

const SIZE = 65536;
let curve = new Float32Array(SIZE);

class GeneticWaveShaper extends Genetic(Effects.Effect) {

    constructor(sequencerEngine) {
        super('Genetic WaveShaper', () => new Tone.Gain(1), sequencerEngine); 

        this.counter=0;
        this.waveShaperNode = new Tone.WaveShaper([-1, 1]);
        this.effectNode.connect(this.waveShaperNode);
        this.postGain = new Tone.Gain(1);
        this.waveShaperNode.connect(this.postGain);

        this.overSampleParameter = new AbstractParameter(
            "oversample", this, (obj, x) => this.waveShaperNode.oversample = x ? '4x' : 'none', {
                min: 0.01,
                max: 32,
                unit: '',
                defaultValue: false,
                constantAutomationRate: true
            });

        this.driveParameter = new AbstractAudioParamParameter(
            'drive', this.effectNode.gain, (obj, drive) => {
                this.effectNode.gain.value = drive;
                this.postGain.gain.value = Math.pow(1 / drive, 0.6);
            },
            {
                documention: "Controls the level of saturation.",
                min: 0.01,
                max: 32,
                unit: '',
                defaultValue: 1,
                constantAutomationRate: true
            });
        this.registerParameter(this.driveParameter);

        this.recomputeCurve();

   }

    get stages() { return 6; }
    get smoothness() { return 6; }

    recomputeCurve() {
        const points = Math.pow(2, this.smoothness+1)*(2*this.stages + 1) + 1
        console.log('points = ', points);
        const curve = new Float32Array(points);
        for (let i = 0; i < points; i++) {
            const x = (2*this.stages + 1)*Math.PI*(i/(points-1) - 0.5);
            curve[i] = Math.sin(x);
        }
        this.waveShaperNode.curve = curve;
    }
    
    get documentation() {
        return "Saturates the sound, making it more distorted.";
    }

    dispose() {
        super.dispose();
        this.postGain.dispose();
        this.waveShaperNode.dispose();
    }

    connect(node) {
        this.postGain.connect(node);
    }

    disconnect(node) {
        this.postGain.disconnect(node);
    }

    getJson() {
        let json = {
            ... super.getJson(),
            drive: this.driveParameter.get()
        };

        json = this.saveParameterToJson(json, "driveParameter", this.driveParameter);
        return json;
    }

    fromJson(json, isCopy) {
        let bulk = super.fromJson(json, isCopy);
        bulkUpdate(this.driveParameter, json.drive, bulk);
        return {
            ... bulk,
            ... this.loadParameterFromJson(json, 'driveParameter', this.driveParameter, isCopy)
        };
    }

    getEffectCopy(sequencerEngine=this.sequencerEngine, ignoreId=true) {
        var copy = new GeneticWaveShaper(sequencerEngine);
        copy.fromJson(this.getJson(), ignoreId);
        return copy;
    }

    generateVariation(coordinates, degree) {
        console.log('generate dregree=', degree);
        let variation = [];
        for (let p of this.waveShaperNode.curve) {
            let _p = Math.min(1,p + degree*Math.random());
            if (Math.random() < degree) {
                _p = 0; //Math.random()*degree;
            }
            variation.push(_p);
        }
        return Float32Array.from(variation);
    };

    breed(a, b) {
        let solution = [];
        for (let i=0; i <this.waveShaperNode.curve.length; i++) {
            let flip = Math.random();
            // bias towards elite
            if (flip < 0.6) {
                solution.push(a[i]);
            } else {
                solution.push(b[i]);
            }
        }
        return Float32Array.from(solution);

    }

    selectVariation(num, time, rampTime) {
        let variation = this.variations[num];
        console.log("selected variation = ", variation);
        this.waveShaperNode.curve = variation;
        this.selectedVariation = num;
    }

    firstReproduction() {
        this.variations = this.generateVariations(this.waveShaperNode.curve);
    }

    getBestCopy() {
        return this.bestSolutions;
                
    }
}

export default GeneticWaveShaper;
